import PortalIdParser from 'PortalIdParser';
import { CrmSearchAllProperties } from 'hs-resources/resources/CrmSearch';
export { CrmSearchAllProperties };
/**
 * The CrmSearchAllProperties resource always fetches every property and hardcodes
 * its own requestOptions, so `properties`, `requestOptions` and `associationSpecs`
 * from our request are intentionally dropped when mapping onto its narrower args.
 */
export function toPulseArgs(fetchParams) {
  var _fetchParams$sorts, _fetchParams$query, _fetchParams$count, _fetchParams$offset;
  return {
    // filterGroups/sorts are wire-identical to the crm-search request the
    // GraphQL path already sends these same values as; the two packages just
    // declare structurally different (but runtime-compatible) filter types.
    filterGroups: fetchParams.filterGroups,
    objectTypeId: fetchParams.objectTypeId,
    sorts: (_fetchParams$sorts = fetchParams.sorts) !== null && _fetchParams$sorts !== void 0 ? _fetchParams$sorts : null,
    query: (_fetchParams$query = fetchParams.query) !== null && _fetchParams$query !== void 0 ? _fetchParams$query : null,
    count: (_fetchParams$count = fetchParams.count) !== null && _fetchParams$count !== void 0 ? _fetchParams$count : null,
    offset: (_fetchParams$offset = fetchParams.offset) !== null && _fetchParams$offset !== void 0 ? _fetchParams$offset : null,
    timeZoneSource: 'USER'
  };
}
export function getPulseArgs(variables) {
  return toPulseArgs(variables.fetchParams);
}
function transformPulseCrmObject(pulseObject) {
  const properties = {};
  for (const key of Object.keys(pulseObject.properties)) {
    const property = pulseObject.properties[key];
    if (property) {
      var _property$value;
      properties[key] = {
        source: property.source,
        sourceId: property.sourceId,
        timestamp: property.timestamp,
        value: (_property$value = property.value) !== null && _property$value !== void 0 ? _property$value : null
      };
    }
  }
  return {
    objectId: pulseObject.objectId,
    objectTypeId: pulseObject.objectTypeId,
    portalId: Number(PortalIdParser.get()),
    properties,
    currentUserPermissions: pulseObject.currentUserPermissions
  };
}

/**
 * Adapts the hs-resources CrmSearchAllProperties payload into the CrmSearchResponse
 * shape the rest of listing-lib expects. Associated objects are not fetched by this
 * resource, so `associatedObjects` is omitted; useHasPulseCrmSearch keeps consumers
 * that request `associationSpecs` off the Pulse path.
 */
export function transformPulseResponseToCrmSearchResponse(pulseData) {
  return pulseData ? toCrmSearchResponse(pulseData) : undefined;
}
function toCrmSearchResponse(pulseData) {
  return {
    total: pulseData.total,
    offset: pulseData.offset,
    hasMore: pulseData.hasMore,
    results: pulseData.results.map(transformPulseCrmObject)
  };
}
function transformCrmObjectToPulseCrmObject(crmObject) {
  const properties = {};
  for (const key of Object.keys(crmObject.properties)) {
    var _property$source, _property$sourceId;
    const property = crmObject.properties[key];
    properties[key] = {
      name: key,
      value: property.value == null ? undefined : String(property.value),
      source: (_property$source = property.source) !== null && _property$source !== void 0 ? _property$source : undefined,
      sourceId: (_property$sourceId = property.sourceId) !== null && _property$sourceId !== void 0 ? _property$sourceId : undefined,
      timestamp: property.timestamp
    };
  }
  return {
    objectId: Number(crmObject.objectId),
    objectTypeId: crmObject.objectTypeId,
    currentUserPermissions: crmObject.currentUserPermissions,
    properties,
    __allProperties: true
  };
}

/**
 * Inverse of transformPulseResponseToCrmSearchResponse. Fields with no analog in
 * CrmSearchResponse (portalId, property `name`, canMerge) are reconstructed; none
 * of them are surfaced downstream, so the round-trip is lossless for consumers.
 */
export function transformCrmSearchResponseToPulseResponse(response) {
  return {
    total: response.total,
    offset: response.offset,
    hasMore: response.hasMore,
    results: response.results.map(transformCrmObjectToPulseCrmObject)
  };
}

/**
 * Applies a CrmSearchResponse-shaped optimistic updater to the stored Pulse cache
 * data by transforming out to the CrmSearchResponse shape, running the updater, and
 * transforming back into the resource's own payload shape.
 */
export function updatePulseCacheData(currentData, updater) {
  return transformCrmSearchResponseToPulseResponse(updater(toCrmSearchResponse(currentData)));
}